import React, { useState } from 'react'
import { KTIcon } from '../../../../../../_metronic/helpers'
import { CopyableCell } from '../../../../../common/CopyableCell'
import { convertUTCtoMSK } from '../../../../../../utils'
import { parseServerInstant } from '../../../../matching/routes/Dashboard/core/mskTime'
import { HOLD_LABELS, REVERSED_LABEL } from '../core/consts'
import { ILedgerOperation } from '../core/response.types'
import { OperationRowMenu } from './OperationRowMenu'
import styles from './OperationRow.module.css'

// Время операции — по МСК, как в фильтре и в выгрузке xlsx, независимо от
// пояса браузера.
const formatDate = (iso: string) => {
  try {
    return convertUTCtoMSK(parseServerInstant(iso).toISOString(), 'dd.MM HH:mm')
  } catch {
    return '—'
  }
}

const DIRECTION_LABEL: Record<'in' | 'out', string> = {
  in: 'Приход',
  out: 'Расход',
}

interface OperationRowProps {
  op: ILedgerOperation
  // Название типа из справочника Ledger; нет его — показываем код.
  kindLabel?: string
}

// Строка списка (не <tr>) в стиле платёжных списков: время → название + ID
// под ним (CopyableCell — обводка/копирование по наведению на сам ID, не на
// всю строку) → бейдж валюты → направление/статус холда → сумма + комиссия
// или резерв под ней → меню действий «⋮» (CPAYMENT-1739).
export const OperationRow: React.FC<OperationRowProps> = ({ op, kindLabel }) => {
  const [expanded, setExpanded] = useState(false)
  const isHoldActive = op.holdState === 'active'
  const hasPricing = op.pricing !== null
  // Отменённый резерв не двигал баланс (net = 0) — показывать для него
  // «Приход/Расход» нечестно, движения не было.
  const showDirection = op.holdState !== 'released'
  // У операции из одних ног резерва тела нет: net и body у неё нули, и
  // показывать надо зарезервированную сумму. holdAmount === null значит, что
  // резерв не записан вовсе, — прочерк, а не ноль.
  const isHoldRow = op.kind === 'hold'

  return (
    <>
      <div className='d-flex align-items-center border-bottom border-gray-200 py-3 px-2'>
        <div
          data-testid='operation-time'
          className='text-muted fs-7'
          style={{ width: 90, flexShrink: 0 }}
        >
          {formatDate(op.startedAt)}
        </div>

        <div className='flex-grow-1 me-3' style={{ minWidth: 0 }}>
          <div className='fw-bold text-truncate'>{kindLabel ?? op.kind}</div>
          <div className='text-muted'>
            <CopyableCell value={op.ticketId} mono />
          </div>
        </div>

        <div style={{ width: 70, flexShrink: 0 }}>
          <span className='badge badge-light-primary'>{op.currency ?? '—'}</span>
        </div>

        <div
          className='d-flex flex-column align-items-start gap-1'
          style={{ width: 150, flexShrink: 0 }}
        >
          {showDirection && (
            <span
              className={`badge ${
                op.direction === 'in' ? 'badge-light-success' : 'badge-light-danger'
              }`}
            >
              {DIRECTION_LABEL[op.direction]}
            </span>
          )}
          {op.holdState === 'active' && (
            <span className='badge badge-light-warning'>{HOLD_LABELS.active}</span>
          )}
          {op.holdState === 'released' && (
            <span className={`badge ${styles.neutralBadge}`}>{HOLD_LABELS.released}</span>
          )}
          {op.reversed && <span className={`badge ${styles.neutralBadge}`}>{REVERSED_LABEL}</span>}
        </div>

        <div className='text-end' style={{ minWidth: 150, flexShrink: 0 }}>
          {isHoldRow ? (
            <>
              <div
                data-testid='operation-amount'
                className={`fw-bold ${isHoldActive ? 'text-muted' : ''}`}
              >
                {isHoldActive && (
                  <span data-testid='operation-hold-icon'>
                    <KTIcon iconName='lock' className='fs-6 me-1' />
                  </span>
                )}
                {op.holdAmount === null ? '—' : `${op.holdAmount} ${op.currency}`}
              </div>
              <div className='text-muted fs-8'>
                {isHoldActive ? 'Ожидает завершения' : 'Движения баланса нет'}
              </div>
            </>
          ) : (
            <>
              <div data-testid='operation-amount' className='fw-bold'>
                {op.net} {op.currency}
              </div>
              <div data-testid='operation-commission' className='text-muted fs-8'>
                Комиссия {op.commission}
              </div>
            </>
          )}
        </div>

        {/* Фиксированная ширина под «⋮» (36px кнопки RowKebabMenu) — колонка
            держит место и когда меню нет (op.pricing === null), иначе строка
            без действий у́же и сумма визуально скачет между рядами. */}
        <div className='ms-2 d-flex justify-content-center' style={{ width: 36, flexShrink: 0 }}>
          <OperationRowMenu
            hasPricing={hasPricing}
            expanded={expanded}
            onToggleExpand={() => setExpanded((v) => !v)}
          />
        </div>
      </div>
      {expanded && op.pricing && (
        <div className='d-flex flex-wrap gap-4 fs-8 py-2 px-2 bg-light-secondary'>
          {op.pricing.rate && <span>Курс: {op.pricing.rate}</span>}
          {op.pricing.bodyRub && <span>Тело RUB: {op.pricing.bodyRub}</span>}
          {op.pricing.bodyUsdt && <span>Тело USDT: {op.pricing.bodyUsdt}</span>}
          {op.pricing.marginUsdt && <span>Маржа ПС: {op.pricing.marginUsdt}</span>}
          {op.pricing.fxRateSource && <span>Источник курса: {op.pricing.fxRateSource}</span>}
        </div>
      )}
    </>
  )
}
